import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useUser } from "../../../../context/UserContext";
import { useHat } from "../../../../context/HatContext";
import { toast } from "sonner";
import { X, Plus, Search, RefreshCcwIcon, CheckIcon, Upload, Crown, Shield, UserCheck, Users, AlertTriangle, Sparkles, Info } from "lucide-react";
import {
  getUsers,
  shareHatWithUser,
  unshareHatFromUser,
  getVAAccessList,
  updateHatManagers,
  assignHatToDepartment,
  checkVAPermissionAssignManagers,
  checkVAPermissionForDepartment,
  checkVAPermissionChangeModel,
  checkVAPermissionShareWithUsers,
  removeHatFromDepartment,
  // Scheduled Hat Execution
  checkGoogleDriveConnection,
  authenticateGoogleDrive,
  GoogleDriveFile,
  createHatSchedule,
  getHatSchedule,
  toggleHatSchedule,
  deleteHatSchedule,
  HatScheduleUnit,
  HatScheduleRow,
  HatScheduleType,
  HatScheduleTimezone,
  HatTaskConfig,
  HatIntervalSchedule,
  HatCronSchedule,
  CreateHatScheduleRequest,
  estimateScheduleCost,
  ScheduleCostEstimate,
  ScheduleCostAlternative,
  // Outlook connection (reused for the Outlook Email Monitor action)
  checkOutlookConnection,
  authenticateOutlook,
  // Automatic model selection
  suggestVAModel,
} from "../../../../service/apiService";
import GoogleDriveAuth from "../../../GoogleDrive/GoogleDriveAuth";
import GoogleDriveFilePicker from "../../../GoogleDrive/GoogleDriveFilePicker";
import OutlookAuth from "../../../Outlook/OutlookAuth";
import { User, VAModelSuggestion } from "../../../../types";
import BrainBankSelector from "./BrainBankSelector";
import ConnectorSelector from "./ConnectorSelector";
import VAManagerSelector from "./VAManagerSelector";
import DepartmentSelector from "./DepartmentSelector"; 
import { useModel } from "../../../../context/ModelContext";
import ModelSelector from "./ModelSelector";  

interface Manager {
  user_id: string;
  username: string;
  email: string;
  name: string;
}

// ── Weekly (day-of-week) schedule helpers ──────────────────────────
// AWS EventBridge day-of-week names, ordered Mon→Sun for the picker.
const WEEKDAYS: { label: string; value: string }[] = [
  { label: "Mon", value: "MON" },
  { label: "Tue", value: "TUE" },
  { label: "Wed", value: "WED" },
  { label: "Thu", value: "THU" },
  { label: "Fri", value: "FRI" },
  { label: "Sat", value: "SAT" },
  { label: "Sun", value: "SUN" },
];

// Build a 6-field EventBridge cron from selected weekdays + "HH:MM" (in the schedule's time zone).
// e.g. (["MON","FRI"], "08:00") -> "0 8 ? * MON,FRI *"
const buildWeeklyCron = (days: string[], time: string): string => {
  const [hh, mm] = (time || "08:00").split(":");
  const minute = parseInt(mm ?? "0", 10) || 0;
  const hour = parseInt(hh ?? "0", 10) || 0;
  const dayList = days.length > 0 ? days.join(",") : "MON-FRI";
  return `${minute} ${hour} ? * ${dayList} *`;
};

// Build a 6-field EventBridge cron for "every day at HH:MM" (in the schedule's time zone).
// e.g. "19:00" -> "0 19 * * ? *"   |   "07:30" -> "30 7 * * ? *"
const buildDailyCron = (time: string): string => {
  const [hh, mm] = (time || "08:00").split(":");
  const minute = parseInt(mm ?? "0", 10) || 0;
  const hour = parseInt(hh ?? "0", 10) || 0;
  return `${minute} ${hour} * * ? *`;
};

// ── Cost estimate formatting ───────────────────────────────────────
// Estimates span several orders of magnitude — a daily VA costs a fraction
// of a cent per run while a 1-minute VA runs into tens of dollars a month —
// so precision is picked per magnitude. A flat 2dp would render every
// per-run figure as "$0.00".
const formatUsd = (value: number): string => {
  if (value >= 1) return `$${value.toFixed(2)}`;
  if (value >= 0.01) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(4)}`;
};

// Whole runs read better than decimals, except for schedules that fire less
// than once a day (e.g. a weekly cron is 0.29 runs/day).
const formatRuns = (value: number): string =>
  value >= 1 ? Math.round(value).toLocaleString() : value.toFixed(2);

// Frequency line under the headline figure. Schedules that fire less than
// once a day are described per month, where the number is meaningful.
// The per-run figure is what makes the headline legible — runs x each =
// monthly — and it's the one number that stays fixed while comparing
// intervals, so it belongs next to the run count.
const describeRuns = (estimate: ScheduleCostEstimate): string => {
  const { runs_per_day, runs_per_month, per_run_cost, schedule_expression } = estimate;
  if (runs_per_day === null || runs_per_month === null) return schedule_expression;
  const frequency =
    runs_per_day >= 1
      ? `${formatRuns(runs_per_day)} runs a day`
      : `~${formatRuns(runs_per_month)} runs a month`;
  const each = per_run_cost !== null ? `${formatUsd(per_run_cost)} each · ` : "";
  return `${frequency} · ${each}${schedule_expression}`;
};

// Expand a MON-FRI style range into individual day tokens (best-effort).
const expandDayRange = (range: string): string[] => {
  const order = WEEKDAYS.map(d => d.value);
  const [a, b] = range.split("-");
  const i = order.indexOf(a);
  const j = order.indexOf(b);
  if (i === -1 || j === -1 || i > j) return ["MON", "TUE", "WED", "THU", "FRI"];
  return order.slice(i, j + 1);
};

interface Hat {
  id?: string;
  hat_name: string;
  department?: string;
  summary: string;
  initial_prompt: string;
  directions: string;
  is_public: boolean;
  model_id?: string; 
  feature_keys: {
    image_creation: { enabled: boolean };
    internet_browsing: { enabled: boolean };
    kyva_llm: { enabled: boolean };
    kyva_connectors: { enabled: boolean; connector_ids?: string[]; };
    include_knowledge_base: { enabled: boolean; knowledge_base_ids: string[]; };
  };
  selected_icon?: File | null;
  userId?: string; 
  user_id?: string; 
  managers?: Manager[];
}

interface CreateVAFormProps {
  vaData?: Hat;
  onSave?: () => void;
  onCancel?: () => void;
  aiGeneratedFields?: Record<string, string> | null;
  onAIFieldsApplied?: () => void;
  formDataRef?: React.MutableRefObject<{ hat_name: string; summary: string; initial_prompt: string; directions: string } | null>;
}

const CreateVAForm: React.FC<CreateVAFormProps> = ({ vaData, onSave, onCancel, aiGeneratedFields, onAIFieldsApplied, formDataRef }) => {
  const { createHat, updateHatData, fetchHatsList, hatsList } = useHat();
  const { userId, userDetails, showEmailAddresses } = useUser();
  const { models } = useModel(); 
  const [loading, setLoading] = useState(false);
  const isOwner = !vaData || (vaData?.userId === userId || vaData?.user_id === userId);
  const isManager = (vaData?.managers as unknown as string[])?.includes(userId) || false; 
  const canEdit = isOwner || userDetails?.role === 'admin' || isManager; 
  const isReadOnly = !!vaData && !canEdit;
  const [creatorInfo, setCreatorInfo] = useState<{id: string; name?: string} | null>(null); 

  // Permission states 
  const [permissions, setPermissions] = useState({
    assign_to_users: true, 
    assign_to_department: true,
    share_with_everyone: true,
    change_model: true
  });
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const isAdmin = userDetails?.role === 'admin';
  
  // Initialize formData with completely clean defaults
  const [formData, setFormData] = useState<Hat>(() => ({
    hat_name: "",
    department: null as any, 
    summary: "",
    initial_prompt: "",
    directions: "",
    is_public: false,
    model_id: undefined,
    feature_keys: {
      image_creation: { enabled: false },
      internet_browsing: { enabled: false },
      kyva_llm: { enabled: false },
      kyva_connectors: { enabled: false, connector_ids: [] },
      include_knowledge_base: { enabled: false, knowledge_base_ids: [] }
    },
    managers: []
  }));

  // Helper function to ensure feature_keys are always properly initialized
const ensureFeatureKeys = (data: any) => {
  const defaultFeatureKeys = {
    image_creation: { enabled: false },
    internet_browsing: { enabled: false },
    kyva_llm: { enabled: false },
    kyva_connectors: { enabled: false, connector_ids: [] },
    include_knowledge_base: { enabled: false, knowledge_base_ids: [] }
  };

  return {
    ...data,
    feature_keys: {
      ...defaultFeatureKeys,
      ...(data.feature_keys || {}),
      image_creation: { enabled: false, ...(data.feature_keys?.image_creation || {}) },
      internet_browsing: { enabled: false, ...(data.feature_keys?.internet_browsing || {}) },
      kyva_llm: { enabled: false, ...(data.feature_keys?.kyva_llm || {}) },
      kyva_connectors: { 
        enabled: false, 
        connector_ids: [], 
        ...(data.feature_keys?.kyva_connectors || {}) 
      },
      include_knowledge_base: { 
        enabled: false, 
        knowledge_base_ids: [], 
        ...(data.feature_keys?.include_knowledge_base || {}) 
      }
    }
  };
};
  
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userQuery, setUserQuery] = useState("");

  // Access list state for showing who already has access
  const [accessList, setAccessList] = useState<{owner: string; managers: string[]; shared_users: string[]} | null>(null);
  const [isLoadingAccess, setIsLoadingAccess] = useState(false);
  const [revokingUserId, setRevokingUserId] = useState<string | null>(null);

  // ── Automatic model selection ──────────────────────────────────
  // The VA's model is chosen for the user based on what the VA actually does,
  // preferring the cheapest model that can handle the workload. A manual pick
  // (or a model already saved on the VA) always wins — auto-selection never
  // overwrites a deliberate choice.
  const [modelSuggestion, setModelSuggestion] = useState<VAModelSuggestion | null>(null);
  const [isSuggestingModel, setIsSuggestingModel] = useState(false);
  const [modelManuallyChosen, setModelManuallyChosen] = useState(false);
  // Signature of the inputs the last suggestion was made from, so editing an
  // unrelated field (or React re-rendering) never re-triggers the LLM call.
  const lastSuggestionInputRef = useRef<string>("");

  const [summaryCharCount, setSummaryCharCount] = useState(0);
  const [initialPromptCharCount, setInitialPromptCharCount] = useState(0);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // ── Scheduled VA Execution (additive, default OFF on create) ───
  // Available on both create and edit. Editing fetches the existing
  // schedule on mount and pre-populates the form.
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleEnabledState, setScheduleEnabledState] = useState(true); // EventBridge ENABLED/DISABLED
  const [existingSchedule, setExistingSchedule] = useState<HatScheduleRow | null>(null);
  const [scheduleAction, setScheduleAction] = useState<"drive-monitor" | "outlook-monitor">("drive-monitor");
  const [scheduleType, setScheduleType] = useState<"interval" | "cron" | "weekly">("interval");
  const [intervalValue, setIntervalValue] = useState<number>(5);
  const [intervalUnit, setIntervalUnit] = useState<HatScheduleUnit>("minutes");
  const [cronExpression, setCronExpression] = useState<string>("0 * * * ? *");
  // Weekly (day-of-week) mode — compiles to an EventBridge cron on save.
  const [weeklyDays, setWeeklyDays] = useState<string[]>(["MON", "TUE", "WED", "THU", "FRI"]);
  const [weeklyTime, setWeeklyTime] = useState<string>("08:00");
  // Daily run time (user-chosen) — the time of day the digest is sent.
  const [dailyTime, setDailyTime] = useState<string>("08:00");
  // Time zone the Weekly / Daily time is read in.
  const [scheduleTimezone, setScheduleTimezone] = useState<HatScheduleTimezone>("UTC");
  const timezoneSelect = (
    <select
      value={scheduleTimezone}
      onChange={e => setScheduleTimezone(e.target.value as HatScheduleTimezone)}
      className="px-2 py-1 border border-gray-300 rounded-md text-xs bg-white"
    >
      <option value="UTC">UTC</option>
      <option value="America/New_York">EST</option>
    </select>
  );
  const [selectedDriveFile, setSelectedDriveFile] = useState<GoogleDriveFile | null>(null);
  const [driveConnected, setDriveConnected] = useState<boolean | null>(null);
  const [showDriveAuth, setShowDriveAuth] = useState(false);
  const [showFilePicker, setShowFilePicker] = useState(false);
  // Outlook Email Monitor action
  const [outlookConnected, setOutlookConnected] = useState<boolean | null>(null);
  const [showOutlookAuth, setShowOutlookAuth] = useState(false);
  const [retryEnabled, setRetryEnabled] = useState(false);
  const [retryMaxCount, setRetryMaxCount] = useState<number>(2);
  const [retryBackoffSec, setRetryBackoffSec] = useState<number>(10);
  const [recipientEmail, setRecipientEmail] = useState<string>("");
  const [scheduleBusy, setScheduleBusy] = useState(false);
  // Estimated running cost of the chosen schedule. Purely informational —
  // it never gates saving, and a failed estimate must not block the form.
  const [costEstimate, setCostEstimate] = useState<ScheduleCostEstimate | null>(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costError, setCostError] = useState<string | null>(null);

  // Swap the schedule to one of the cheaper intervals offered beside the
  // estimate. Every alternative is a plain rate(), so this is a straight
  // write to the interval control — the estimate then re-prices itself.
  const applyAlternative = (alternative: ScheduleCostAlternative) => {
    setScheduleType("interval");
    setIntervalValue(alternative.interval_value);
    setIntervalUnit(alternative.interval_unit);
  };

  // Fetch existing schedule when editing
  useEffect(() => {
    if (!vaData?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getHatSchedule(vaData.id!);
        console.log("[Schedule] GET /hat-admin/schedule response:", resp);
        if (cancelled) return;
        if (resp.data) {
          setExistingSchedule(resp.data);
          setScheduleEnabled(true);
          setScheduleEnabledState(resp.data.enabled);
          // Action (skill) — default drive-monitor for legacy rows
          if (resp.data.skill === "outlook-monitor") {
            setScheduleAction("outlook-monitor");
          } else {
            setScheduleAction("drive-monitor");
          }
          setScheduleTimezone(resp.data.timezone === "America/New_York" ? "America/New_York" : "UTC");
          // Parse "rate(N units)" or "cron(...)"
          const sv = resp.data.schedule_value;
          if (resp.data.schedule_type === "interval") {
            setScheduleType("interval");
            const m = /^rate\((\d+)\s+(\w+)\)$/.exec(sv);
            if (m) {
              setIntervalValue(parseInt(m[1], 10));
              const unit = m[2].endsWith("s") ? m[2] : `${m[2]}s`;
              if (unit === "minutes" || unit === "hours" || unit === "days") {
                setIntervalUnit(unit as HatScheduleUnit);
              }
            }
          } else {
            const m = /^cron\((.*)\)$/.exec(sv);
            const inner = m ? m[1] : "";
            // Weekly shape: "M H ? * DAYS *" → show the friendly Weekly mode.
            const wm = /^(\d+)\s+(\d+)\s+\?\s+\*\s+([A-Za-z,\-]+)\s+\*$/.exec(inner);
            // Daily shape: "M H * * ? *" → show the daily time picker.
            const dm = /^(\d+)\s+(\d+)\s+\*\s+\*\s+\?\s+\*$/.exec(inner);
            // Drive has no daily time picker, so its daily crons stay in Cron mode.
            const isDrive = resp.data.skill !== "outlook-monitor";
            if (wm) {
              setScheduleType("weekly");
              const hour = wm[2].padStart(2, "0");
              const minute = wm[1].padStart(2, "0");
              setWeeklyTime(`${hour}:${minute}`);
              const dayField = wm[3].toUpperCase();
              setWeeklyDays(dayField.includes("-") ? expandDayRange(dayField) : dayField.split(","));
            } else if (dm && !isDrive) {
              setScheduleType("interval");
              setDailyTime(`${dm[2].padStart(2, "0")}:${dm[1].padStart(2, "0")}`);
            } else {
              setScheduleType("cron");
              if (m) setCronExpression(inner);
            }
          }
          setRetryEnabled(!!resp.data.retry_enabled);
          setRetryMaxCount(resp.data.retry_max_count || 2);
          setRetryBackoffSec(resp.data.retry_backoff_sec || 10);
          // Pre-populate the Drive file from task_config (joined by backend)
          const tc = resp.data.task_config;
          if (tc?.google_drive_file_id && tc?.file_name) {
            setSelectedDriveFile({
              id: tc.google_drive_file_id,
              name: tc.file_name,
              mimeType: tc.file_mime_type || "",
            });
          }
          if (tc?.recipient_email) {
            setRecipientEmail(tc.recipient_email);
          }
        } else {
          console.log("[Schedule] No existing schedule for this VA");
        }
      } catch (err) {
        console.error("[Schedule] Failed to load existing schedule:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [vaData?.id]);

  // Drive connection check whenever schedule section is active
  useEffect(() => {
    if (!scheduleEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        const ok = await checkGoogleDriveConnection();
        if (!cancelled) setDriveConnected(ok);
      } catch {
        if (!cancelled) setDriveConnected(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scheduleEnabled]);

  const handleDriveAuthSuccess = async (code: string) => {
    try {
      await authenticateGoogleDrive(code);
      setDriveConnected(true);
      toast.success("Google Drive connected");
    } catch (err) {
      console.error("Google Drive auth failed:", err);
      toast.error("Failed to connect Google Drive");
    } finally {
      setShowDriveAuth(false);
    }
  };

  // Outlook connection check whenever the Outlook action is active
  useEffect(() => {
    if (!scheduleEnabled || scheduleAction !== "outlook-monitor") return;
    let cancelled = false;
    (async () => {
      try {
        const ok = await checkOutlookConnection();
        if (!cancelled) setOutlookConnected(ok);
      } catch {
        if (!cancelled) setOutlookConnected(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scheduleEnabled, scheduleAction]);

  const handleOutlookAuthSuccess = async (code: string) => {
    try {
      await authenticateOutlook(code);
      setOutlookConnected(true);
      toast.success("Outlook connected");
    } catch (err) {
      console.error("Outlook auth failed:", err);
      toast.error("Failed to connect Outlook");
    } finally {
      setShowOutlookAuth(false);
    }
  };

  // ── Schedule payload helpers (shared by create + edit save paths) ──
  // Whether the schedule section has everything required to be saved.
  const isScheduleReady = (): boolean => {
    if (!scheduleEnabled) return false;
    if (!recipientEmail.trim()) return false;
    if (scheduleAction === "drive-monitor") return !!selectedDriveFile;
    return outlookConnected === true; // outlook-monitor
  };

  const scheduleNotReadyMessage = (): string =>
    scheduleAction === "drive-monitor"
      ? "Select a Drive file and a recipient email to save the schedule."
      : "Connect Outlook and set a recipient email to save the schedule.";

  // The schedule half of the payload, on its own. Split out so the cost
  // estimate is priced from the *exact* same compilation the save path
  // uses — the two can't drift — and so a schedule can be priced before
  // the rest of the form (Drive file, recipient) is filled in.
  const buildScheduleSpec = (): {
    schedule_type: HatScheduleType;
    interval?: HatIntervalSchedule;
    cron?: HatCronSchedule;
  } => {
    // Weekly compiles to a cron. Outlook's daily option also compiles to a cron
    // so the digest fires at the user's chosen time instead of a drifting rate().
    const outlookDaily = scheduleAction === "outlook-monitor" && scheduleType === "interval";
    const effectiveType: HatScheduleType =
      scheduleType === "weekly" || outlookDaily ? "cron" : scheduleType;
    const cronExpr =
      scheduleType === "weekly" ? buildWeeklyCron(weeklyDays, weeklyTime)
      : outlookDaily            ? buildDailyCron(dailyTime)
      : cronExpression;

    return {
      schedule_type: effectiveType,
      // Outlook daily goes out as a cron (above), so only Drive uses a real interval.
      interval: (scheduleType === "interval" && !outlookDaily)
        ? { interval_value: intervalValue, interval_unit: intervalUnit }
        : undefined,
      cron: effectiveType === "cron" ? { expression: cronExpr } : undefined,
    };
  };

  const buildSchedulePayload = (hatId: string, hatName: string): CreateHatScheduleRequest => {
    const task_config: HatTaskConfig =
      scheduleAction === "drive-monitor"
        ? {
            google_drive_file_id: selectedDriveFile!.id,
            file_name: selectedDriveFile!.name,
            file_mime_type: selectedDriveFile!.mimeType || "",
            recipient_email: recipientEmail.trim(),
          }
        : {
            recipient_email: recipientEmail.trim(),
            folder: "Inbox",
          };

    return {
      hat_id: hatId,
      hat_name: hatName,
      skill: scheduleAction,
      task_config,
      ...buildScheduleSpec(),
      // Every schedule mode shows the UTC/EST picker.
      timezone: scheduleTimezone,
      retry: {
        enabled: retryEnabled,
        max_count: retryEnabled ? retryMaxCount : 0,
        backoff_sec: retryEnabled ? retryBackoffSec : 0,
      },
    };
  };

  // Re-price the schedule whenever any input feeding the expression changes.
  // Debounced so typing an interval or a cron doesn't fire a request per
  // keystroke; `cancelled` guards against a slow response overwriting a
  // newer one. `hat_id` is only present when editing — that's what lets the
  // backend swap the seeded duration for this VA's observed run history.
  useEffect(() => {
    if (!scheduleEnabled) {
      setCostEstimate(null);
      setCostError(null);
      setCostLoading(false);
      return;
    }
    let cancelled = false;
    setCostLoading(true);
    const timer = setTimeout(async () => {
      try {
        const estimate = await estimateScheduleCost({
          ...buildScheduleSpec(),
          skill: scheduleAction,
          hat_id: vaData?.id,
        });
        if (!cancelled) {
          setCostEstimate(estimate);
          setCostError(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setCostEstimate(null);
          setCostError(
            err?.response?.data?.detail ||
              "Couldn't estimate the cost of this schedule."
          );
        }
      } finally {
        if (!cancelled) setCostLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scheduleEnabled,
    scheduleAction,
    scheduleType,
    intervalValue,
    intervalUnit,
    cronExpression,
    weeklyDays,
    weeklyTime,
    dailyTime,
    vaData?.id,
  ]);

  const fetchPermissions = async () => {
    // Admin users always have all permissions
    if (isAdmin) {
      setPermissions({
        assign_to_users: true,
        assign_to_department: true,
        share_with_everyone: true,
        change_model: true 
      });
      return;
    }
    
    try {
      setPermissionsLoading(true);
      
      const [assignUsersPermission, assignDeptPermission, shareUsersPermission, changeModelPermission] = await Promise.all([
        checkVAPermissionAssignManagers(),
        checkVAPermissionForDepartment(),
        checkVAPermissionShareWithUsers(),
        checkVAPermissionChangeModel() 
      ]);
      
      setPermissions({
        assign_to_users: assignUsersPermission,
        assign_to_department: assignDeptPermission,
        share_with_everyone: shareUsersPermission,
        change_model: changeModelPermission 
      });
    } catch (error) {
      console.error('Error fetching VA permissions:', error);
      // On error, set restrictive permissions for regular users
      setPermissions({
        assign_to_users: false,
        assign_to_department: false,
        share_with_everyone: false,
        change_model: false
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Helper to check if user has permission (admin always returns true)
  const hasPermission = (permissionType: keyof typeof permissions) => {
    return isAdmin || permissions[permissionType];
  };

  // ── Automatic model selection ──────────────────────────────────
  // BrainBanks and connectors are optional inputs: only the ones actually
  // switched on are sent, so a VA with neither is judged purely on its summary
  // and instructions.
  const buildModelSelectionInput = useCallback(() => {
    const kb = formData.feature_keys?.include_knowledge_base;
    const connectors = formData.feature_keys?.kyva_connectors;
    return {
      hat_name: formData.hat_name.trim(),
      summary: formData.summary.trim(),
      directions: formData.directions.trim(),
      knowledge_base_ids: kb?.enabled ? (kb.knowledge_base_ids || []) : [],
      connector_ids: connectors?.enabled ? (connectors.connector_ids || []) : [],
      internet_browsing: formData.feature_keys?.internet_browsing?.enabled || false,
    };
  }, [
    formData.hat_name,
    formData.summary,
    formData.directions,
    formData.feature_keys,
  ]);

  const runModelSuggestion = useCallback(async (input: ReturnType<typeof buildModelSelectionInput>) => {
    setIsSuggestingModel(true);
    try {
      const suggestion = await suggestVAModel(input);
      setModelSuggestion(suggestion);
      setFormData(prev => ({ ...prev, model_id: suggestion.model_id }));
    } catch (error: any) {
      // Selection is an assist, not a gate — a failure leaves the field as it
      // was and the user can still pick a model by hand.
      console.error("Automatic model selection failed:", error);
      throw error;
    } finally {
      setIsSuggestingModel(false);
    }
  }, []);

  // Auto-select as soon as the VA is described well enough to judge. Debounced
  // so it fires once the user stops typing rather than on every keystroke.
  useEffect(() => {
    if (isReadOnly || permissionsLoading || !hasPermission('change_model')) return;
    // Editing an existing VA never re-picks the model. The VA keeps whatever it
    // was built with; the re-check button below is the only way to change it.
    if (vaData) return;
    if (modelManuallyChosen) return;

    const input = buildModelSelectionInput();
    if (!input.summary || !input.directions) return;

    const signature = JSON.stringify(input);
    if (signature === lastSuggestionInputRef.current) return;

    const timer = setTimeout(() => {
      // Recorded before the request so an in-flight call is never duplicated.
      lastSuggestionInputRef.current = signature;
      runModelSuggestion(input).catch(() => {
        // Allow a retry on the next edit if this attempt failed.
        lastSuggestionInputRef.current = "";
      });
    }, 1500);

    return () => clearTimeout(timer);
  }, [
    buildModelSelectionInput,
    modelManuallyChosen,
    isReadOnly,
    permissionsLoading,
    permissions.change_model,
    isAdmin,
    runModelSuggestion,
    vaData,
  ]);

  // Explicit re-run — also the way back to automatic selection after the user
  // has overridden or cleared the model by hand.
  const handleRecheckModel = async () => {
    const input = buildModelSelectionInput();
    if (!input.summary || !input.directions) {
      toast.warning("Add a summary and instructions first so the model can be matched to the task.");
      return;
    }
    try {
      lastSuggestionInputRef.current = JSON.stringify(input);
      setModelManuallyChosen(false);
      await runModelSuggestion(input);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || "Could not select a model automatically. Please choose one manually.");
    }
  };

useEffect(() => {
  if (vaData) {
    console.log("Loading existing VA data:", vaData);
    
    // Ensure feature_keys are properly initialized
    const safeVaData = ensureFeatureKeys(vaData);
    
    setFormData({
      ...safeVaData,
      initial_prompt: vaData.initial_prompt || "",
      department: (vaData.department && vaData.department.trim() !== "") ? vaData.department : null as any,
      selected_icon: null,
      managers: vaData.managers || []
    });
    setSummaryCharCount(vaData.summary ? vaData.summary.length : 0);
    setInitialPromptCharCount(vaData.initial_prompt ? vaData.initial_prompt.length : 0);

    // A VA that already has a model keeps it. Whoever saved it made that call
    // deliberately, so auto-selection must not quietly replace it on open.
    setModelManuallyChosen(!!(vaData.model_id && vaData.model_id.trim()));

    // Set creator info
    const creatorId = vaData.userId || vaData.user_id;
    if (creatorId) {
      setCreatorInfo({ id: creatorId });
    }
  }
}, [vaData]);

  // Apply AI-generated fields when they arrive from the controller
  useEffect(() => {
    if (aiGeneratedFields) {
      setFormData(prev => ({
        ...prev,
        ...(aiGeneratedFields.hat_name !== undefined && { hat_name: aiGeneratedFields.hat_name }),
        ...(aiGeneratedFields.summary !== undefined && { summary: aiGeneratedFields.summary }),
        ...(aiGeneratedFields.initial_prompt !== undefined && { initial_prompt: aiGeneratedFields.initial_prompt }),
        ...(aiGeneratedFields.directions !== undefined && { directions: aiGeneratedFields.directions }),
      }));
      if (aiGeneratedFields.summary !== undefined) {
        setSummaryCharCount(aiGeneratedFields.summary.length);
      }
      if (aiGeneratedFields.initial_prompt !== undefined) {
        setInitialPromptCharCount(aiGeneratedFields.initial_prompt.length);
      }
      if (onAIFieldsApplied) {
        onAIFieldsApplied();
      }
    }
  }, [aiGeneratedFields]);

  // Keep formDataRef in sync so the controller can read current form values
  useEffect(() => {
    if (formDataRef) {
      formDataRef.current = {
        hat_name: formData.hat_name,
        summary: formData.summary,
        initial_prompt: formData.initial_prompt,
        directions: formData.directions,
      };
    }
  }, [formData.hat_name, formData.summary, formData.initial_prompt, formData.directions, formDataRef]);

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const result = await getUsers();
        setUsers(result.filter(user => user.id !== userId));
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to load users");
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [userId]);

  // Fetch access list for existing VAs
  const fetchAccessList = async () => {
    if (!vaData?.id) return;
    setIsLoadingAccess(true);
    try {
      const result = await getVAAccessList(vaData.id);
      setAccessList(result);
    } catch (error) {
      console.error("Error fetching access list:", error);
    } finally {
      setIsLoadingAccess(false);
    }
  };

  useEffect(() => {
    if (vaData?.id) {
      fetchAccessList();
    }
  }, [vaData?.id]);

  // Handle revoking shared access from a user
  const handleRevokeAccess = async (revokeUserId: string) => {
    if (!vaData?.id) return;
    setRevokingUserId(revokeUserId);
    try {
      await unshareHatFromUser(revokeUserId, [vaData.id]);
      toast.success(`Access revoked for ${revokeUserId}`);
      await fetchAccessList();
    } catch (error) {
      console.error("Error revoking access:", error);
      toast.error(`Failed to revoke access for ${revokeUserId}`);
    } finally {
      setRevokingUserId(null);
    }
  };

  useEffect(() => {
    if (userDetails) {
      fetchPermissions();
    }
  }, [userDetails?.role]);
  
  // Set of user IDs who already have access (for filtering the share picker)
  const alreadyHasAccess = useMemo(() => {
    if (!accessList) return new Set<string>();
    const ids = new Set<string>();
    if (accessList.owner) ids.add(accessList.owner);
    accessList.managers.forEach(id => ids.add(id));
    accessList.shared_users.forEach(id => ids.add(id));
    return ids;
  }, [accessList]);

  const filteredUsers = useMemo(() => {
  let result: User[];
  // Admin can always see all matching users
  if (userDetails?.role === 'admin') {
    result = userQuery === ""
      ? users
      : users.filter(user =>
          user.id.toLowerCase().includes(userQuery.toLowerCase())
        );
  } else if (showEmailAddresses) {
    // When toggle is ON (Show side) - users can browse normally
    result = userQuery === ""
      ? users
      : users.filter(user =>
          user.id.toLowerCase().includes(userQuery.toLowerCase())
        );
  } else {
    // When toggle is OFF (Hide side) - users need exact match
    result = userQuery === ""
      ? [] // Return empty array instead of all users
      : users.filter(user =>
          user.id.toLowerCase() === userQuery.toLowerCase() // Exact match only
        );
  }
  // Exclude users who already have access to this VA
  if (alreadyHasAccess.size > 0) {
    result = result.filter(user => !alreadyHasAccess.has(user.id));
  }
  return result;
}, [users, userQuery, userDetails?.role, showEmailAddresses, alreadyHasAccess]);
  
  // Handle input changes with validation
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
        
    // Special handling for summary field to ensure it's never empty string when required
    if (name === "summary") {
      const trimmedValue = value.trim();
      setFormData(prev => ({
        ...prev,
        [name]: value 
      }));
      setSummaryCharCount(value.length);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      if (name === "initial_prompt") { 
        setInitialPromptCharCount(value.length);
      }
    }
  };
  
  const handleToggle = (name: string) => {
  if (name === "is_public") {
    setFormData(prev => ({
      ...prev,
      is_public: !prev.is_public
    }));
  } else if (name === "kyva_connectors") {
    setFormData(prev => ({
      ...prev,
      feature_keys: {
        ...prev.feature_keys,
        kyva_connectors: {
          enabled: !(prev.feature_keys?.kyva_connectors?.enabled || false),
          connector_ids: !(prev.feature_keys?.kyva_connectors?.enabled || false) ? [] : prev.feature_keys?.kyva_connectors?.connector_ids || []
        }
      }
    }));
  } else {
    // Handle feature keys with safety checks
    setFormData(prev => {
      const currentFeature = prev.feature_keys?.[name as keyof typeof prev.feature_keys] as any;
      const currentEnabled = currentFeature?.enabled || false;
      
      return {
        ...prev,
        feature_keys: {
          ...prev.feature_keys,
          [name]: {
            ...(currentFeature || {}),
            enabled: !currentEnabled
          }
        }
      };
    });
  }
};
  
  // Toggle user selection
  const toggleUserSelection = (user: User) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };
  
  // Handle removing a selected user
  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(prev => prev.filter(user => user.id !== userId));
  };

   const handleManagerAdded = (manager: Manager) => {
    setFormData(prev => ({
      ...prev,
      managers: [...(prev.managers || []), manager]
    }));
  };

  const handleManagerRemoved = (managerId: string) => {
    setFormData(prev => ({
      ...prev,
      managers: (prev.managers || []).filter(m => m.user_id !== managerId)
    }));
  };

  // Add these handler functions for BrainBank selection
const handleKnowledgeBaseSelected = (knowledgeBaseId: string) => {
  setFormData(prev => ({
    ...prev,
    feature_keys: {
      ...prev.feature_keys,
      include_knowledge_base: {
        ...prev.feature_keys.include_knowledge_base,
        knowledge_base_ids: [
          ...prev.feature_keys.include_knowledge_base.knowledge_base_ids,
          knowledgeBaseId
        ]
      }
    }
  }));
};

const handleKnowledgeBaseRemoved = (knowledgeBaseId: string) => {
  setFormData(prev => ({
    ...prev,
    feature_keys: {
      ...prev.feature_keys,
      include_knowledge_base: {
        ...prev.feature_keys.include_knowledge_base,
        knowledge_base_ids: prev.feature_keys.include_knowledge_base.knowledge_base_ids.filter(
          id => id !== knowledgeBaseId
        )
      }
    }
  }));
};

// Handler functions for Connector selection
const handleConnectorSelected = (connectorId: string) => {
  setFormData(prev => ({
    ...prev,
    feature_keys: {
      ...prev.feature_keys,
      kyva_connectors: {
        ...prev.feature_keys.kyva_connectors,
        connector_ids: [
          ...(prev.feature_keys.kyva_connectors.connector_ids || []),
          connectorId
        ]
      }
    }
  }));
};

const handleConnectorRemoved = (connectorId: string) => {
  setFormData(prev => ({
    ...prev,
    feature_keys: {
      ...prev.feature_keys,
      kyva_connectors: {
        ...prev.feature_keys.kyva_connectors,
        connector_ids: (prev.feature_keys.kyva_connectors.connector_ids || []).filter(
          id => id !== connectorId
        )
      }
    }
  }));
};

//  Department handlers with proper undefined handling
const handleDepartmentSelected = (departmentName: string) => {
  console.log(`Department selected: "${departmentName}"`);
  const trimmedName = departmentName.trim();
  setFormData(prev => ({
    ...prev,
    department: trimmedName || (null as any) // Send null if empty
  }));
};

const handleDepartmentRemoved = () => {
  console.log(" Department removed");
  setFormData(prev => ({
    ...prev,
    department: null as any // Send null to backend to indicate removal
  }));
};

const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target.files?.[0];
  if (file) {
    setImportFile(file);
    processImportFile(file);
  }
};

const processImportFile = (file: File) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const jsonContent = e.target?.result as string;
      const importData = JSON.parse(jsonContent);
      
      // Validate required fields
      if (!importData.virtual_assistant_name || !importData.summary || !importData.instructions) {
        toast.error("Invalid import file: Missing required fields");
        return;
      }
      
      // Map imported data to form fields
      setFormData(prev => ({
        ...prev,
        hat_name: importData.virtual_assistant_name || "",
        summary: importData.summary || "",
        initial_prompt: importData.initial_prompt || "",
        directions: importData.instructions || ""
      }));
      
      // Update character counts
      setSummaryCharCount(importData.summary?.length || 0);
      setInitialPromptCharCount(importData.initial_prompt?.length || 0);
      
      toast.success("Virtual Assistant data imported successfully!");
      setShowImportModal(false);
    } catch (error) {
      console.error("Error parsing import file:", error);
      toast.error("Invalid JSON file format");
    }
  };
  reader.readAsText(file);
};

const handleDragEnter = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  setDragActive(true);
};

const handleDragLeave = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  setDragActive(false);
};

const handleDragOver = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const handleDrop = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  setDragActive(false);
  
  const files = e.dataTransfer.files;
  if (files && files.length > 0) {
    const file = files[0];
    
    // Check if it's a JSON file
    if (file.type === 'application/json' || file.name.endsWith('.json')) {
      setImportFile(file);
      processImportFile(file);
    } else {
      toast.error("Please drop a JSON file only");
    }
  }
};
  
  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  
  // Enhanced validation with better error messages
  const trimmedHatName = formData.hat_name.trim();
  const trimmedSummary = formData.summary.trim();
  const trimmedDirections = formData.directions.trim();
  
  console.log(" Form submission validation:", {
    hat_name: `"${trimmedHatName}"`,
    summary: `"${trimmedSummary}"`,
    directions: `"${trimmedDirections}"`,
    department: formData.department
  });
  
  if (!trimmedHatName) {
    toast.error("VA name is required");
    return;
  }
  
  if (!trimmedSummary) {
    toast.error("Summary is required and cannot be empty");
    return;
  }
  
  if (!trimmedDirections) {
    toast.error("Instructions are required");
    return;
  }
  
  try {
    setLoading(true);
    
    if (vaData?.id) {
  // Editing existing VA
  //✅ Explicitly include model_id in update (even if empty/undefined)
  const updatePayload = {
    ...formData,
    model_id: formData.model_id || ""  // Convert undefined/null to empty string
  };
  console.log("📤 Update payload being sent:", updatePayload);
  await updateHatData(vaData.id, updatePayload);

      const originalDepartment = vaData.department;
  const newDepartment = formData.department && formData.department.trim() !== "" 
    ? formData.department.trim() 
    : null;

  // Case 1: Department was removed (had department, now empty/undefined)
  if (originalDepartment && !newDepartment) {
    try {
      await removeHatFromDepartment(vaData.id);
      toast.success("VA removed from department");
    } catch (error) {
      console.error("Error removing VA from department:", error);
      toast.error("Failed to remove VA from department");
    }
  }
  // Case 2: Department was assigned or changed (preserves existing functionality)
  else if (newDepartment && newDepartment !== originalDepartment) {
    try {
      await assignHatToDepartment(vaData.id, newDepartment, userId);
      toast.success(`VA assigned to ${newDepartment} department`);
    } catch (error) {
      console.error("Error assigning VA to department:", error);
      toast.error("Failed to assign VA to department");
    }
  }
      
      if (selectedUsers.length > 0) {
        try {
          await Promise.all(
            selectedUsers.map(user =>
              shareHatWithUser(user.id, [vaData.id!])
            )
          );
          toast.success(`Virtual Assistant shared with ${selectedUsers.length} user(s)`);
          setSelectedUsers([]);
          await fetchAccessList();
        } catch (error) {
          console.error("Error sharing VA:", error);
          toast.error("Failed to share Virtual Assistant with selected users");
        }
      }

      // ── Edit-mode schedule sync ─────────────────────────────────
      // Decide between create / update / delete based on toggle vs existing.
      try {
        const hadSchedule = !!existingSchedule;
        if (scheduleEnabled && !isScheduleReady()) {
          toast.warning(scheduleNotReadyMessage());
        } else if (scheduleEnabled) {
          // create or update
          await createHatSchedule(buildSchedulePayload(vaData.id!, trimmedHatName));
          // Match the saved enabled/disabled state if user changed it
          if (hadSchedule && existingSchedule!.enabled !== scheduleEnabledState) {
            await toggleHatSchedule(vaData.id!, scheduleEnabledState);
          }
          toast.success(hadSchedule ? "Schedule updated" : "Schedule created");
        } else if (hadSchedule && !scheduleEnabled) {
          // user turned off the schedule toggle → delete it
          await deleteHatSchedule(vaData.id!);
          setExistingSchedule(null);
          toast.success("Schedule removed");
        }
      } catch (err: any) {
        console.error("Error syncing schedule:", err);
        const detail = err?.response?.data?.detail || err?.message || "Unknown error";
        toast.warning(`VA updated but schedule sync failed: ${detail}`);
      }
    } else {
      //  Creating new VA with properly cleaned data
      const cleanedFormData = {
        ...formData,
        hat_name: trimmedHatName,
        summary: trimmedSummary, 
        directions: trimmedDirections,
        initial_prompt: formData.initial_prompt.trim(),
        //  Only include department if it's actually set and not empty
        department: (formData.department && formData.department.trim() !== "") 
          ? formData.department.trim() 
          : "" ,
          model_id: formData.model_id
      };
            
      // Log the exact payload being sent to backend
      console.log("Payload to createHat:", {
        hat_name: cleanedFormData.hat_name,
        department: cleanedFormData.department,
        summary: cleanedFormData.summary,
        initial_prompt: cleanedFormData.initial_prompt,
        directions: cleanedFormData.directions,
        is_public: cleanedFormData.is_public,
        feature_keys: cleanedFormData.feature_keys
      });
      
      const createdVA = await createHat(cleanedFormData);
      
      console.log("Created VA from context:", createdVA);
      
      const hatId = createdVA?.hat_id || createdVA?.id;
      
      // Add managers to the newly created VA (check permission first)
      if (hatId && formData.managers && formData.managers.length > 0 && hasPermission('assign_to_users')) {
        try {
          const managerUserIds = formData.managers.map(m => m.user_id);
          await updateHatManagers(hatId, managerUserIds);
          console.log("Managers added to new VA:", managerUserIds);
        } catch (error) {
          console.error("Error adding managers to new VA:", error);
          toast.error("VA created but failed to add managers");
        }
      }
      
        if (selectedUsers.length > 0 && hatId && hasPermission('share_with_everyone')) {
        try {
          await Promise.all(
            selectedUsers.map(user =>
              shareHatWithUser(user.id, [hatId])
            )
          );
          toast.success(`Virtual Assistant created and shared with ${selectedUsers.length} user(s)`);
        } catch (error) {
          console.error("Error sharing newly created VA:", error);
          toast.error("Virtual Assistant created but failed to share with selected users");
        }
      } else if (selectedUsers.length > 0 && !hatId) {
        console.log("Created VA response:", createdVA);
        toast.warning("Virtual Assistant created, but sharing failed. Please use the Share feature.");
      }

      // ── Scheduled VA Execution (only if user opted in) ──────────
      // Wrapped so a scheduling failure never rolls back VA creation.
      if (scheduleEnabled && hatId) {
        if (!isScheduleReady()) {
          toast.warning(`VA created, but the schedule was not saved. ${scheduleNotReadyMessage()}`);
        } else {
          try {
            const resp = await createHatSchedule(buildSchedulePayload(hatId, trimmedHatName));
            toast.success(`Schedule created (${resp.schedule_expression})`);
          } catch (err: any) {
            console.error("Error creating VA schedule:", err);
            const detail = err?.response?.data?.detail || err?.message || "Unknown error";
            toast.warning(`VA created but schedule failed: ${detail}`);
          }
        }
      }
    }
    
    // Success messages
     if (!vaData && selectedUsers.length === 0 && (!formData.managers || formData.managers.length === 0) && !formData.department) {
      toast.success("Virtual Assistant created successfully");
    } else if (!vaData) {
      let message = "Virtual Assistant created";
      const features = [];
      if (formData.managers && formData.managers.length > 0) {
        features.push(`${formData.managers.length} manager(s)`);
      }
      if (formData.department && formData.department.trim() !== "") {
        features.push(`assigned to ${formData.department}`);
      }
      if (features.length > 0) {
        message += ` with ${features.join(' and ')}`;
      }
      toast.success(message);
    } else if (vaData) {
      toast.success("Virtual Assistant updated successfully");
    }
    
    if (onSave) {
      onSave();
    }
  } catch (error) {
    console.error("Error saving VA:", error);
    // Better error handling with specific error messages
    if (error && typeof error === 'object' && 'response' in error) {
      const apiError = error as any;
      const errorMessage = apiError.response?.data?.detail || apiError.message || `Failed to ${vaData ? "update" : "create"} Virtual Assistant`;
      toast.error(errorMessage);
    } else {
      toast.error(`Failed to ${vaData ? "update" : "create"} Virtual Assistant`);
    }
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      {isReadOnly && (
        <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-start">
          <AlertTriangle className="h-5 w-5 text-amber-500 mr-3 mt-0.5" />
          <div>
            <p className="text-sm font-medium">You don't have permission to edit this Virtual Assistant.</p>
            <p className="text-sm">Contact the Virtual Assistant Manager to make changes.</p>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit}>
       {!isReadOnly && (
       <div className="mb-6">
        <div 
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive 
              ? 'border-violet-500 bg-violet-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
         <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-2">
           <span className="font-medium">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-gray-500 mb-4">
           JSON files only
         </p>
         <button
           type="button"
           onClick={() => setShowImportModal(true)}
           className="inline-flex items-center px-4 py-2 text-sm font-medium text-violet-600 bg-white border border-violet-200 rounded-md hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
          <Upload className="h-4 w-4 mr-2" />
           Choose File
        </button>
       </div>
      </div>
       )}
       {/* VA Name */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Virtual Assistant Name:
          </label>
          <div className="relative">
            <input
              type="text"
              name="hat_name"
              value={formData.hat_name}
              onChange={handleChange}
              disabled={isReadOnly}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 disabled:opacity-60 disabled:bg-gray-50 disabled:cursor-not-allowed"
              placeholder="VA Name..."
              required
            />
          </div>
        </div>
        
        {/* Summary with better validation visual feedback */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Summary: <span className="text-red-500">*</span>
            </label>
            <span className={`text-xs ${summaryCharCount === 0 ? 'text-red-500' : 'text-gray-500'}`}>
              {summaryCharCount}/200 {summaryCharCount === 0 && '(Required)'}
            </span>
          </div>
          <textarea
            name="summary"
            value={formData.summary}
            onChange={handleChange}
            maxLength={200}
            rows={2}
            disabled={isReadOnly}
            className={`w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 disabled:opacity-60 disabled:bg-gray-50 disabled:cursor-not-allowed ${
              summaryCharCount === 0 && !isReadOnly ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Short summary for users... (Required)"
            required
          />
          {summaryCharCount === 0 && (
            <p className="text-xs text-red-500 mt-1">Summary is required and cannot be empty</p>
          )}
        </div>

        {/* Initial Prompt */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Initial Prompt:
            </label>
            <span className="text-xs text-gray-500">
              {initialPromptCharCount}/500
            </span>
          </div>
          <textarea
            name="initial_prompt"
            value={formData.initial_prompt}
            onChange={handleChange}
            maxLength={500}
            rows={3}
            disabled={isReadOnly}
            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 disabled:opacity-60 disabled:bg-gray-50 disabled:cursor-not-allowed"
            placeholder="Default prompt that will appear when users select this VA..."
          />
          <p className="text-xs text-gray-500 mt-1">
            This prompt will auto-populate in the message input when users select this Virtual Assistant. They can modify it before sending.
          </p>
        </div>
        
                {/* Instructions */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Instructions:<span className="text-red-500">*</span>
          </label>
          <textarea
            name="directions"
            value={formData.directions}
            onChange={handleChange}
            rows={4}
            disabled={isReadOnly}
            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 disabled:opacity-60 disabled:bg-gray-50 disabled:cursor-not-allowed"
            placeholder="Detailed instructions for the AI to follow..."required
          />
        </div>

        {/* Two-column layout for Managers and Departments */}
        {(hasPermission('assign_to_users') || hasPermission('assign_to_department')) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* VA Managers Section */}
                   {hasPermission('assign_to_users') && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">VA Managers:</h3>
                <div className="border border-gray-200 rounded-md p-4 h-48 overflow-y-auto">
                  {/* Show creator info if available */}
                  {creatorInfo && (
                    <div className="mb-3 bg-blue-50 rounded-lg p-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-xs font-medium bg-blue-100 text-blue-800 py-0.5 px-2 rounded">Creator</span>
                          <span className="text-sm ml-2 text-gray-700">{creatorInfo.id}</span>
                        </div>
                      </div>
                    </div>
                  )}
            
                  <VAManagerSelector
                    hatId={vaData?.id}
                    isEnabled={true}
                    selectedManagers={formData.managers || []}
                    onManagerAdded={handleManagerAdded}
                    onManagerRemoved={handleManagerRemoved}
                    isOwnerOrAdmin={canEdit}
                    userRole={userDetails?.role}
                  />
                </div>
              </div>
            )}
          
                {/* Department Section with clear optional indication */}
            {hasPermission('assign_to_department') && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Department <span className="text-gray-400 text-xs"></span>:
                </h3>
                <div className="border border-gray-200 rounded-md p-4 h-48 overflow-y-auto">
                  <DepartmentSelector
                    selectedDepartmentName={formData.department || ""} 
                    onDepartmentSelected={handleDepartmentSelected}
                    onDepartmentRemoved={handleDepartmentRemoved}
                    isEnabled={!isReadOnly}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  You can assign this VA to a department or leave it unassigned.
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Two-column layout for Users and BrainBanks */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                   {/* Share with individual users */}
          {hasPermission('share_with_everyone') && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Share with individual users:</h3>

              {/* Current Access Section - only show for existing VAs */}
              {vaData?.id && (
                <div className="border border-gray-200 rounded-lg p-3 mb-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 text-gray-500" />
                      Current Access
                      {accessList && (accessList.managers.length + accessList.shared_users.length) > 0 && (
                        <span className="text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">
                          {accessList.managers.length + accessList.shared_users.length}
                        </span>
                      )}
                    </h4>
                  </div>

                  {isLoadingAccess ? (
                    <div className="flex justify-center py-3">
                      <RefreshCcwIcon className="animate-spin h-4 w-4 text-gray-400" />
                    </div>
                  ) : accessList ? (
                    <div className="space-y-1.5 max-h-32 overflow-y-auto">
                      {/* Owner */}
                      <div className="flex items-center justify-between py-1 px-2 bg-white rounded border border-gray-100">
                        <div className="flex items-center gap-1.5">
                          <Crown className="h-3 w-3 text-amber-500" />
                          <span className="text-xs text-gray-700">{accessList.owner}</span>
                        </div>
                        <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                          Owner
                        </span>
                      </div>

                      {/* Managers */}
                      {accessList.managers.map(managerId => (
                        <div key={`mgr-${managerId}`} className="flex items-center justify-between py-1 px-2 bg-white rounded border border-gray-100">
                          <div className="flex items-center gap-1.5">
                            <Shield className="h-3 w-3 text-blue-500" />
                            <span className="text-xs text-gray-700">{managerId}</span>
                          </div>
                          <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
                            Manager
                          </span>
                        </div>
                      ))}

                      {/* Shared Users */}
                      {accessList.shared_users.map(sharedUserId => (
                        <div key={`shared-${sharedUserId}`} className="flex items-center justify-between py-1 px-2 bg-white rounded border border-gray-100">
                          <div className="flex items-center gap-1.5">
                            <UserCheck className="h-3 w-3 text-violet-500" />
                            <span className="text-xs text-gray-700">{sharedUserId}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded-full font-medium">
                              Shared
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRevokeAccess(sharedUserId)}
                              disabled={revokingUserId === sharedUserId}
                              className="text-gray-400 hover:text-red-500 focus:outline-none transition-colors"
                              title="Revoke access"
                            >
                              {revokingUserId === sharedUserId ? (
                                <RefreshCcwIcon className="animate-spin h-3 w-3" />
                              ) : (
                                <X className="h-3 w-3" />
                              )}
                            </button>
                          </div>
                        </div>
                      ))}

                      {accessList.managers.length === 0 && accessList.shared_users.length === 0 && (
                        <p className="text-xs text-gray-400 text-center py-1">
                          No additional users have access yet
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              {userDetails?.role !== 'admin' && !showEmailAddresses && (
                <p className="text-xs text-gray-500 mb-2">
                  Enter the complete email address to find and select users.
                </p>)}
              <div className="border border-gray-200 rounded-md p-4 h-48 overflow-y-auto">
                {/* Search input */}
                <div className="relative mb-3">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="w-full pl-9 pr-3 py-1.5 border border-gray-200 text-sm text-gray-700 bg-white rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
                    placeholder={userDetails?.role === 'admin' 
                        ? "Search users..." 
                        : showEmailAddresses 
                          ? "Search users..."
                          : "Enter full email address..."}
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                  />
                  {isLoadingUsers && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                      <RefreshCcwIcon className="animate-spin h-4 w-4 text-gray-400" />
                    </div>
                  )}
                </div>
                
                {/* Selected users chips */}
                {selectedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedUsers.map(user => (
                      <div 
                        key={`selected-${user.id}`}
                        className="flex items-center bg-violet-50 text-violet-700 text-xs rounded-full px-2 py-1"
                      >
                        <span className="mr-1">{user.id}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveUser(user.id)}
                          className="text-violet-500 hover:text-violet-700 focus:outline-none"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Users list */}
                <div className="space-y-1 mt-2">
                  {isLoadingUsers ? (
                    <div className="flex justify-center py-2">
                      <RefreshCcwIcon className="animate-spin h-5 w-5 text-gray-400" />
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="text-sm text-gray-500 text-center py-2">
                      {userDetails?.role === 'admin' 
                        ? (userQuery ? "No users found" : "No users available")
                        : showEmailAddresses 
                          ? (userQuery ? "No users found" : "No users available")
                          : (userQuery === ""
                              ? "Please enter the full email address to find and select a user."
                              : "No users found. Please enter the complete email address."
                          )
                        }
                    </div>
                  ) : (
                    filteredUsers.map(user => {
                      const isSelected = selectedUsers.some(u => u.id === user.id);
                      return (
                        <div 
                          key={user.id}
                          className={`flex items-center justify-between py-1.5 px-2 rounded ${
                            isSelected ? "bg-violet-50" : ""
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`user-${user.id}`}
                              checked={isSelected}
                              onChange={() => toggleUserSelection(user)}
                              className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                            />
                            <label 
                              htmlFor={`user-${user.id}`}
                              className="text-sm text-gray-700 cursor-pointer"
                            >
                              {user.id}
                            </label>
                          </div>
                          {isSelected && (
                            <CheckIcon className="h-4 w-4 text-violet-600" />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
          
    {/* Associated BrainBanks */}
   <div>
  <div className="flex justify-between items-center mb-2">
    <h3 className="text-sm font-medium text-gray-700">Associated BrainBanks:</h3>
    <div className="flex items-center">
      <input
        type="checkbox"
        id="include_knowledge_base"
        checked={formData.feature_keys.include_knowledge_base.enabled || false}
        onChange={() => handleToggle("include_knowledge_base")}
        disabled={isReadOnly}
        className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded disabled:opacity-60 disabled:cursor-not-allowed"
      />
      <label htmlFor="include_knowledge_base" className="ml-2 text-xs text-gray-700">
        Enable
      </label>
    </div>
  </div>
  <div className="border border-gray-200 rounded-md p-4 h-48 overflow-y-auto">
    <BrainBankSelector
      selectedKnowledgeBaseIds={formData.feature_keys.include_knowledge_base.knowledge_base_ids}
      onKnowledgeBaseSelected={handleKnowledgeBaseSelected}
      onKnowledgeBaseRemoved={handleKnowledgeBaseRemoved}
      isEnabled={!isReadOnly && formData.feature_keys.include_knowledge_base.enabled}
    />
  </div>
</div>
 </div>
    {/* Features */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Features:</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="internet_browsing"
                checked={formData.feature_keys.internet_browsing.enabled || false}
                onChange={() => handleToggle("internet_browsing")}
                disabled={isReadOnly}
                className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded disabled:opacity-60 disabled:cursor-not-allowed"
              />
              <label htmlFor="internet_browsing" className="ml-2 text-sm text-gray-700">
                Internet Browsing
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="kyva_connectors"
                checked={formData.feature_keys.kyva_connectors.enabled || false}
                onChange={() => handleToggle("kyva_connectors")}
                disabled={isReadOnly}
                className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded disabled:opacity-60 disabled:cursor-not-allowed"
              />
              <label htmlFor="kyva_connectors" className="ml-2 text-sm text-gray-700">
                Kyva Connectors
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_public"
                checked={formData.is_public}
                onChange={() => handleToggle("is_public")}
                disabled={isReadOnly}
                className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded disabled:opacity-60 disabled:cursor-not-allowed"
              />
              <label htmlFor="is_public" className="ml-2 text-sm text-gray-700">
                Make VA Available to Everyone
              </label>
            </div>
          </div>

          {/* ─────────── Scheduled VA Execution (opt-in, available on create + edit) ─────────── */}
          <div className="mb-6 pt-4" style={{ maxWidth: "700px" }}>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="schedule_va"
                  checked={scheduleEnabled}
                  onChange={() => setScheduleEnabled(v => !v)}
                  disabled={isReadOnly}
                  className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded disabled:opacity-60 disabled:cursor-not-allowed"
                />
                <label htmlFor="schedule_va" className="ml-2 text-sm font-medium text-gray-800">
                  Schedule this VA to run automatically
                </label>
                {existingSchedule && !scheduleEnabled && (
                  <span className="ml-3 text-xs text-orange-600">
                    Will remove the existing schedule on Save.
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Runs an action on a recurring schedule without user interaction.
              </p>

              {scheduleEnabled && (
                <div className="mt-4 ml-6 space-y-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                  {/* Editing mode: schedule status + enable/disable */}
                  {existingSchedule && (
                    <div className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
                      <div className="text-xs text-gray-600">
                        Current: <span className="font-mono">{existingSchedule.schedule_value}</span>
                        {existingSchedule.updated_at && (
                          <span className="ml-2 text-gray-400">(updated {existingSchedule.updated_at})</span>
                        )}
                      </div>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={scheduleEnabledState}
                          onChange={() => setScheduleEnabledState(v => !v)}
                          className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded mr-2"
                        />
                        Enabled
                      </label>
                    </div>
                  )}
                  {/* Action selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                    <select
                      value={scheduleAction}
                      onChange={e => {
                        const val = e.target.value as "drive-monitor" | "outlook-monitor";
                        setScheduleAction(val);
                        // Outlook is a daily digest — force day-based interval.
                        if (val === "outlook-monitor") setIntervalUnit("days");
                      }}
                      disabled={isReadOnly}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white text-gray-700 disabled:opacity-60"
                    >
                      <option value="drive-monitor">Drive Monitor</option>
                      <option value="outlook-monitor">Outlook Email Monitor</option>
                    </select>
                    {scheduleAction === "outlook-monitor" && (
                      <p className="text-xs text-gray-500 mt-1">
                        Emails you an AI summary of new Outlook emails received since the last run.
                        Works with any schedule below — every few minutes, hourly, daily, or a
                        weekly weekday-morning digest (on Monday it covers back to Friday).
                      </p>
                    )}
                  </div>

                  {/* Drive connection + file picker (Drive Monitor only) */}
                  {scheduleAction === "drive-monitor" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Google Drive
                    </label>
                    <div className="flex items-center gap-3">
                      {driveConnected === true ? (
                        <span className="text-sm text-green-700">✓ Connected</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowDriveAuth(true)}
                          className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Connect Google Drive
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowFilePicker(true)}
                        disabled={driveConnected !== true}
                        className="px-3 py-1.5 text-sm bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Choose file
                      </button>
                    </div>
                    {selectedDriveFile && (
                      <div className="mt-2 text-xs text-gray-700 bg-white border border-gray-200 rounded px-2 py-1">
                        <span className="font-medium">{selectedDriveFile.name}</span>
                        <span className="text-gray-400 ml-2">({selectedDriveFile.id})</span>
                      </div>
                    )}
                  </div>
                  )}

                  {/* Outlook connection (Outlook Email Monitor only) */}
                  {scheduleAction === "outlook-monitor" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Outlook
                    </label>
                    <div className="flex items-center gap-3">
                      {outlookConnected === true ? (
                        <span className="text-sm text-green-700">✓ Connected</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowOutlookAuth(true)}
                          className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Connect Outlook
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Connect the Microsoft Outlook account whose inbox will be analyzed.
                    </p>
                  </div>
                  )}

                  {/* Recipient email (per-VA). Sender is hardcoded to info@kyva.ai server-side. */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {scheduleAction === "outlook-monitor"
                        ? "Recipient email for the daily digest"
                        : "Recipient email for change alerts"}
                      <span className="text-red-500 ml-1">*</span>
                      {existingSchedule && !recipientEmail.trim() && (
                        <span className="ml-2 text-xs font-normal text-amber-700">
                          (not set — please add one)
                        </span>
                      )}
                    </label>
                    <input
                      type="email"
                      value={recipientEmail}
                      onChange={e => setRecipientEmail(e.target.value)}
                      placeholder="alice@yourcompany.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Recipient email addresses must be verified in AWS SES before use. Unverified emails will be rejected.
                    </p>
                  </div>

                  {/* Schedule type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
                    <div className="flex items-center gap-4 mb-2">
                      <label className="flex items-center text-sm">
                        <input
                          type="radio"
                          name="schedule_type"
                          value="interval"
                          checked={scheduleType === "interval"}
                          onChange={() => setScheduleType("interval")}
                          className="mr-1"
                        />
                        Interval
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="radio"
                          name="schedule_type"
                          value="weekly"
                          checked={scheduleType === "weekly"}
                          onChange={() => setScheduleType("weekly")}
                          className="mr-1"
                        />
                        Weekly
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="radio"
                          name="schedule_type"
                          value="cron"
                          checked={scheduleType === "cron"}
                          onChange={() => setScheduleType("cron")}
                          className="mr-1"
                        />
                        Cron
                      </label>
                    </div>

                    {scheduleType === "interval" && scheduleAction === "outlook-monitor" && (
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Every day at</span>
                          <input
                            type="time"
                            value={dailyTime}
                            onChange={e => setDailyTime(e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-white"
                          />
                          {timezoneSelect}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          The digest email is sent every day at this time (Monday covers Fri–Sun).
                          For specific weekdays only, use Weekly.
                        </p>
                      </div>
                    )}

                    {scheduleType === "interval" && scheduleAction !== "outlook-monitor" && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Every</span>
                        <input
                          type="number"
                          min={1}
                          max={1440}
                          value={intervalValue}
                          onChange={e => setIntervalValue(parseInt(e.target.value, 10) || 1)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm"
                        />
                        <select
                          value={intervalUnit}
                          onChange={e => setIntervalUnit(e.target.value as HatScheduleUnit)}
                          className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-white"
                        >
                          <option value="minutes">minutes</option>
                          <option value="hours">hours</option>
                          <option value="days">days</option>
                        </select>
                        {timezoneSelect}
                      </div>
                    )}

                    {scheduleType === "weekly" && (
                      <div>
                        <div className="flex flex-wrap items-center gap-1.5 mb-2">
                          {WEEKDAYS.map(d => {
                            const selected = weeklyDays.includes(d.value);
                            return (
                              <button
                                key={d.value}
                                type="button"
                                onClick={() =>
                                  setWeeklyDays(prev =>
                                    prev.includes(d.value)
                                      ? prev.filter(x => x !== d.value)
                                      : [...prev, d.value]
                                  )
                                }
                                className={`px-2.5 py-1 text-xs rounded-md border ${
                                  selected
                                    ? "bg-violet-600 text-white border-violet-600"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                {d.label}
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">at</span>
                          <input
                            type="time"
                            value={weeklyTime}
                            onChange={e => setWeeklyTime(e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-white"
                          />
                          {timezoneSelect}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Runs on the selected day(s) at this time ({scheduleTimezone === "UTC" ? "UTC" : "EST"}).
                          {weeklyDays.length === 0 && " Select at least one day (defaults to Mon–Fri)."}
                        </p>
                      </div>
                    )}

                    {scheduleType === "cron" && (
                      <div>
                        <input
                          type="text"
                          value={cronExpression}
                          onChange={e => setCronExpression(e.target.value)}
                          placeholder="0 * * * ? *"
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm font-mono"
                        />
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-gray-600">Time zone</span>
                          {timezoneSelect}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          AWS EventBridge cron — 6 fields (min hr dom mon dow year). 5-field unix cron also accepted.
                        </p>
                      </div>
                    )}

                    {/* ── Estimated cost ─────────────────────────────────
                        Sits under whichever schedule mode is active. The
                        headline is the decision, the cheaper options make
                        the trade-off actionable, and the detail lives behind
                        the ⓘ so it never competes with the number. */}
                    <div className="mt-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
                      {costLoading ? (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <RefreshCcwIcon className="h-3.5 w-3.5 animate-spin" />
                          Estimating cost…
                        </div>
                      ) : costError ? (
                        <div className="flex items-start gap-1.5 text-xs text-amber-700">
                          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
                          <span>{costError}</span>
                        </div>
                      ) : costEstimate ? (
                        <>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              {costEstimate.monthly_cost !== null ? (
                                <p
                                  className={`text-xl font-semibold leading-tight ${
                                    // The backend flags an unusually frequent
                                    // schedule; colour carries that signal
                                    // without a nagging warning banner.
                                    costEstimate.warnings.length > 0
                                      ? "text-amber-600"
                                      : "text-gray-900"
                                  }`}
                                >
                                  ≈ {formatUsd(costEstimate.monthly_cost)}
                                  <span className="text-sm font-normal text-gray-500">
                                    {" "}/ month
                                  </span>
                                </p>
                              ) : (
                                <p className="text-sm font-medium text-gray-500">
                                  Cost can't be estimated for this schedule
                                </p>
                              )}
                              <p className="mt-0.5 text-xs text-gray-500 truncate">
                                {describeRuns(costEstimate)}
                              </p>
                            </div>

                            {/* Detail on demand — hover, or keyboard focus. */}
                            <div className="relative group flex-shrink-0">
                              <button
                                type="button"
                                aria-label="What this estimate includes"
                                className="rounded-full p-1 text-gray-300 hover:text-gray-500 focus:text-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                              >
                                <Info className="h-4 w-4" />
                              </button>
                              <div className="pointer-events-none absolute right-0 top-8 z-20 hidden w-72 rounded-lg bg-gray-900 px-3 py-2.5 text-xs leading-relaxed text-gray-100 shadow-lg group-hover:block group-focus-within:block">
                                <p>{costEstimate.summary}</p>
                                <p className="mt-1.5 text-gray-400">{costEstimate.basis}</p>
                              </div>
                            </div>
                          </div>

                          {/* Cheaper intervals, one tap to apply. Hidden for
                              Outlook, whose "Interval" mode is a daily digest
                              time rather than a rate — a rate chip there would
                              silently do the wrong thing. */}
                          {costEstimate.alternatives.length > 0 &&
                            scheduleAction !== "outlook-monitor" && (
                              <div className="mt-3 border-t border-gray-100 pt-2.5">
                                <p className="mb-1.5 text-xs font-medium text-gray-500">
                                  Costs less if you run it
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {costEstimate.alternatives.map(alternative => (
                                    <button
                                      key={alternative.label}
                                      type="button"
                                      onClick={() => applyAlternative(alternative)}
                                      className="group rounded-md border border-gray-200 px-2.5 py-1.5 text-left transition-colors hover:border-violet-300 hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-violet-200"
                                    >
                                      <span className="block text-xs font-medium text-gray-700 group-hover:text-violet-700">
                                        {alternative.label}
                                      </span>
                                      <span className="block text-xs text-gray-500 group-hover:text-violet-600">
                                        {formatUsd(alternative.monthly_cost)}/mo
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Only the can't-price case still needs words — a
                              busy schedule is already signalled by the amber
                              headline and the cheaper options above. */}
                          {costEstimate.monthly_cost === null &&
                            costEstimate.warnings.map((warning, i) => (
                              <p key={i} className="mt-1.5 text-xs text-gray-500">
                                {warning}
                              </p>
                            ))}
                        </>
                      ) : null}
                    </div>
                  </div>

                  {/* Retry */}
                  <div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="retry_enabled"
                        checked={retryEnabled}
                        onChange={() => setRetryEnabled(v => !v)}
                        className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                      />
                      <label htmlFor="retry_enabled" className="ml-2 text-sm text-gray-700">
                        Retry on failure
                      </label>
                    </div>
                    {retryEnabled && (
                      <div className="flex items-center gap-3 mt-2 ml-6">
                        <label className="text-sm text-gray-600">Max attempts:</label>
                        <input
                          type="number"
                          min={1}
                          max={10}
                          value={retryMaxCount}
                          onChange={e => setRetryMaxCount(parseInt(e.target.value, 10) || 1)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded-md text-sm"
                        />
                        <label className="text-sm text-gray-600">Backoff (sec):</label>
                        <input
                          type="number"
                          min={0}
                          max={3600}
                          value={retryBackoffSec}
                          onChange={e => setRetryBackoffSec(parseInt(e.target.value, 10) || 0)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>

          {/* Connectors Section - Show when Kyva Connectors is enabled */}
        {formData.feature_keys.kyva_connectors.enabled && (
          <div className="mb-6" style={{ maxWidth: '432px' }}>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Available Connectors:</h3>
            <div className="border border-gray-200 rounded-md p-4 max-h-64 overflow-y-auto">
              <ConnectorSelector
                isEnabled={!isReadOnly && formData.feature_keys.kyva_connectors.enabled}
                selectedConnectorIds={formData.feature_keys.kyva_connectors.connector_ids || []}
                onConnectorSelected={handleConnectorSelected}
                onConnectorRemoved={handleConnectorRemoved}
              />
            </div>
          </div>
        )}
        </div>

        {/* Default Model Section - Only show if user has permission.
            Placed last, after BrainBanks and Connectors: the model is chosen
            from those selections, so it reads as the outcome of the VA's
            configuration rather than another thing to configure. */}
        {hasPermission('change_model') && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-700">
                Default Model:
              </h3>
              <div className="flex items-center gap-3">
                {isSuggestingModel && (
                  <span className="flex items-center gap-1.5 text-xs text-amber-600">
                    <RefreshCcwIcon className="animate-spin h-3 w-3" />
                    Choosing the best model...
                  </span>
                )}
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={handleRecheckModel}
                    disabled={isSuggestingModel}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Re-run automatic model selection using the current summary, instructions, BrainBanks and connectors"
                  >
                    <Sparkles className="h-3 w-3" />
                    Choose for me
                  </button>
                )}
              </div>
            </div>

            {/* Outcome of automatic selection */}
            {modelSuggestion && (
              <div className="mb-2 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-md">
                <Sparkles className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800">
                  {modelManuallyChosen ? (
                    <>
                      AI suggested <span className="font-medium">{modelSuggestion.display_name}</span> for
                      this VA — you've selected a different model.
                    </>
                  ) : (
                    <>
                      <span className="font-medium">{modelSuggestion.display_name}</span> selected
                      automatically. {modelSuggestion.reason} You can pick a different model below.
                    </>
                  )}
                </p>
              </div>
            )}

            <div className="border border-gray-200 rounded-md p-4 max-h-96 overflow-y-auto">
              <ModelSelector
                selectedModelId={formData.model_id}
                onModelSelected={(modelId) => {
                  // A deliberate pick pins the model — auto-selection stops
                  // overwriting it until the user asks for a re-check.
                  setModelManuallyChosen(true);
                  setFormData(prev => ({
                    ...prev,
                    model_id: modelId
                  }));
                }}
                onModelCleared={() => {
                  setModelManuallyChosen(true);
                  setFormData(prev => ({
                    ...prev,
                    model_id: undefined
                  }));
                }}
                isEnabled={!isReadOnly}
                requireToolSupport={true}
                recommendedModelId={modelSuggestion?.model_id}
                recommendationReason={modelSuggestion?.reason}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              The most cost-effective model for this VA is selected automatically from its
              summary, instructions, BrainBanks and connectors. Override it any time.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none"
          >
            {isReadOnly ? "Close" : "Cancel"}
          </button>
          {!isReadOnly && (
          <button
            type="submit"
            disabled={loading || (vaData && !canEdit)}
            className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none ${
              loading || (vaData && !canEdit)
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-violet-600 hover:bg-violet-700"
            }`}
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {vaData ? "Updating..." : "Creating..."}
              </span>
            ) : (
              vaData ? "Update Virtual Assistant" : "Create Virtual Assistant"
            )}
          </button>
          )}
        </div>
      </form>
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Import Virtual Assistant</h3>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select JSON file to import:
              </label>
             <input
              type="file"
              accept=".json"
              onChange={handleImportFile}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
             />
           <div className="mt-2 text-xs text-gray-500">
            Or drag and drop a JSON file on the import area above
          </div>
            </div>
            <div className="text-sm text-gray-500 mb-4">
              <p>Import a previously exported Virtual Assistant JSON file.</p>
              <p>This will auto-populate the form fields.</p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled VA Execution — Drive auth + file picker modals */}
      <GoogleDriveAuth
        isOpen={showDriveAuth}
        onClose={() => setShowDriveAuth(false)}
        onAuthSuccess={handleDriveAuthSuccess}
      />
      <GoogleDriveFilePicker
        isOpen={showFilePicker}
        onClose={() => setShowFilePicker(false)}
        onFileSelect={(file) => {
          setSelectedDriveFile(file);
          setShowFilePicker(false);
        }}
      />
      {/* Scheduled VA Execution — Outlook auth modal (Outlook Email Monitor) */}
      <OutlookAuth
        isOpen={showOutlookAuth}
        onClose={() => setShowOutlookAuth(false)}
        onAuthSuccess={handleOutlookAuthSuccess}
      />
    </div>
  );
};
export default CreateVAForm;